import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, makeStyles, Spinner } from '@fluentui/react-components';
import { Dismiss20Regular, Save20Regular } from '@fluentui/react-icons';
import { useDataService, useAsync } from '../app/DataServiceContext';
import { useAppToast } from '../app/ToastContext';
import { questionsCount } from '../lib/pollLabels';
import {
    firstError,
    hasErrors,
    IPollFormValues,
    pollToForm,
    toUpdateRequest,
    validatePollForm,
} from '../components/forms/pollForm';
import { useGeo } from '../components/forms/useGeo';
import PollDetailsFields from '../components/forms/PollDetailsFields';
import PollSettingsFields from '../components/forms/PollSettingsFields';
import QuestionsEditor from '../components/forms/QuestionsEditor';
import PageHeader from '../components/ui/PageHeader';
import Section from '../components/ui/Section';
import ErrorBar from '../components/ui/ErrorBar';
import FormFooter from '../components/ui/FormFooter';
import StatusBadge from '../components/ui/StatusBadge';
import { BlockSkeleton } from '../components/ui/Skeletons';

const useStyles = makeStyles({
    stack: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        '@media (max-width: 767px)': {
            gap: '16px',
        },
    },
});

const PollEditPage: React.FC = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const toast = useAppToast();
    const dataService = useDataService();
    const { id = '' } = useParams<{ id: string }>();

    const poll = useAsync(() => dataService.getPoll(id), [id]);
    const [values, setValues] = React.useState<IPollFormValues>();
    const [showErrors, setShowErrors] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string>();
    const geo = useGeo(values?.geoRegionId ?? '');

    // Seed the form once the poll arrives; later reloads must not wipe edits.
    React.useEffect(() => {
        if (poll.data && !values) setValues(pollToForm(poll.data));
    }, [poll.data, values]);

    const patch = (p: Partial<IPollFormValues>): void => setValues((prev) => (prev ? { ...prev, ...p } : prev));
    const errors = React.useMemo(() => (values ? validatePollForm(values) : undefined), [values]);
    const detailsTo = `/polls/${encodeURIComponent(id)}`;

    const save = async (): Promise<void> => {
        if (!values || !errors) return;
        if (hasErrors(errors)) {
            setShowErrors(true);
            setSaveError(undefined);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        try {
            setSaving(true);
            setSaveError(undefined);
            await dataService.updatePoll(id, toUpdateRequest(values));
            toast.success('Изменения сохранены');
            navigate(detailsTo);
        } catch (e) {
            setSaveError((e as Error)?.message ?? 'Не удалось сохранить изменения.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <PageHeader
                backTo={{ to: detailsTo, label: 'К опросу' }}
                title="Редактирование опроса"
                subtitle={poll.data?.Title}
                badges={poll.data && <StatusBadge state={poll.data.State} />}
            />

            <ErrorBar message={poll.error} title="Не удалось загрузить опрос" onRetry={poll.reload} />
            {showErrors && errors && hasErrors(errors) && (
                <ErrorBar message={firstError(errors)} title="Проверьте форму" onDismiss={() => setShowErrors(false)} />
            )}
            <ErrorBar message={saveError} title="Не удалось сохранить" onDismiss={() => setSaveError(undefined)} />

            {!values && poll.loading && (
                <Section><BlockSkeleton lines={8} /></Section>
            )}

            {values && (
                <div className={styles.stack}>
                    <Section title="Об опросе">
                        <PollDetailsFields values={values} onChange={patch} geo={geo} errors={showErrors ? errors : undefined} />
                    </Section>

                    <Section title="Вопросы" description={values.questions.length ? questionsCount(values.questions.length) : 'Добавьте хотя бы один вопрос'}>
                        <QuestionsEditor
                            questions={values.questions}
                            onChange={(questions) => patch({ questions })}
                            errors={errors?.questionErrors}
                            showErrors={showErrors}
                            listError={errors?.questions}
                        />
                    </Section>

                    <Section title="Правила и сроки">
                        <PollSettingsFields values={values} onChange={patch} datesError={showErrors ? errors?.dates : undefined} />
                    </Section>

                    <FormFooter
                        start={(
                            <Button appearance="subtle" icon={<Dismiss20Regular />} disabled={saving} onClick={() => navigate(detailsTo)}>
                                Отмена
                            </Button>
                        )}
                        end={(
                            <Button appearance="primary" icon={saving ? <Spinner size="tiny" /> : <Save20Regular />} disabled={saving} onClick={() => { void save(); }}>
                                {saving ? 'Сохраняем' : 'Сохранить'}
                            </Button>
                        )}
                    />
                </div>
            )}
        </>
    );
};

export default PollEditPage;
